import {
  Stethoscope,
  TrendingUp,
  AlertTriangle,
  Search,
  UserCheck,
  Calendar,
  Shield,
  Activity,
  Heart,
  Zap,
  Brain as BrainIcon,
  Bug,
} from "lucide-react";
import InteractiveQA from "./InteractiveQA";
import RefinedAnalysis from "./RefinedAnalysis";

const AIAnalysisRenderer = ({
  analysis,
  patientName,
  onSubmitAnswers,
  refinedAnalysis,
  qaAnswers,
}) => {
  // Handle fallback for unstructured response
  if (analysis.error) {
    return (
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-2 text-yellow-800">
            <AlertTriangle className="h-5 w-5 mt-0.5" />
            <div>
              <span className="font-medium">{analysis.message}</span>
              <p className="text-sm mt-1">
                Open browser console (F12) for detailed parsing information.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-5">
          <div className="whitespace-pre-wrap text-gray-700 leading-relaxed text-sm">
            {analysis.rawContent}
          </div>
        </div>
      </div>
    );
  }

  const getConfidenceBadge = (level) => {
    const styles = {
      high: "bg-red-100 text-red-800 border border-red-200",
      medium: "bg-yellow-100 text-yellow-800 border border-yellow-200",
      low: "bg-green-100 text-green-800 border border-green-200",
    };

    const labels = {
      high: "High Confidence",
      medium: "Medium Confidence",
      low: "Low Confidence",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          styles[level] || styles.medium
        }`}
      >
        {labels[level] || level}
      </span>
    );
  };

  const getRiskBadge = (level) => {
    const styles = {
      high: "bg-red-100 text-red-800 border border-red-200",
      medium: "bg-yellow-100 text-yellow-800 border border-yellow-200",
      low: "bg-green-100 text-green-800 border border-green-200",
    };

    const labels = {
      high: "High Risk",
      medium: "Medium Risk",
      low: "Low Risk",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          styles[level] || styles.medium
        }`}
      >
        {labels[level] || level}
      </span>
    );
  };

  const getUrgencyBadge = (urgency) => {
    const styles = {
      urgent: "bg-red-100 text-red-800 border border-red-200",
      high: "bg-red-100 text-red-800 border border-red-200",
      routine: "bg-blue-100 text-blue-800 border border-blue-200",
      medium: "bg-yellow-100 text-yellow-800 border border-yellow-200",
      low: "bg-green-100 text-green-800 border border-green-200",
    };

    const labels = {
      urgent: "Urgent",
      high: "High Priority",
      routine: "Routine",
      medium: "Medium Priority",
      low: "Low Priority",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          styles[urgency] || styles.routine
        }`}
      >
        {labels[urgency] || urgency}
      </span>
    );
  };

  const getRiskIcon = (riskType) => {
    const icons = {
      cardiovascular: Heart,
      infectious: Bug,
      metabolic: Activity,
      neurological: BrainIcon,
    };

    const IconComponent = icons[riskType] || Activity;
    return <IconComponent className="h-5 w-5" />;
  };

  return (
    <div className="space-y-6">
      {/* Differential Diagnosis */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Stethoscope className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Differential Diagnosis
            </h3>
            <p className="text-sm text-gray-600">
              Potential conditions based on clinical presentation
            </p>
          </div>
        </div>
        <div className="space-y-3">
          {analysis.differentialDiagnosis?.map((diagnosis, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium text-gray-900">
                  {diagnosis.condition}
                </h4>
                {getConfidenceBadge(diagnosis.confidence)}
              </div>
              <p className="text-sm text-gray-600">{diagnosis.reasoning}</p>
            </div>
          )) || (
            <p className="text-gray-500 italic">
              No differential diagnoses provided
            </p>
          )}
        </div>
      </div>

      {/* Clinical Patterns */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <TrendingUp className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Clinical Patterns
            </h3>
            <p className="text-sm text-gray-600">
              Trends and patterns identified across visits
            </p>
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-gray-700">
            {analysis.clinicalPatterns || "No patterns identified"}
          </p>
        </div>
      </div>

      {/* Risk Stratification */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Risk Stratification
            </h3>
            <p className="text-sm text-gray-600">
              Assessment of various clinical risks
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {analysis.riskStratification &&
            Object.entries(analysis.riskStratification).map(
              ([riskType, risk]) => (
                <div key={riskType} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {getRiskIcon(riskType)}
                      <h4 className="font-medium text-gray-900 capitalize">
                        {riskType}
                      </h4>
                    </div>
                    {getRiskBadge(risk.level)}
                  </div>
                  <p className="text-sm text-gray-600">{risk.details}</p>
                </div>
              )
            )}
        </div>
      </div>

      {/* Immediate Concerns */}
      {analysis.immediateConcerns &&
        Array.isArray(analysis.immediateConcerns) &&
        analysis.immediateConcerns.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Immediate Concerns
                </h3>
                <p className="text-sm text-gray-600">
                  Issues requiring urgent attention
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {analysis.immediateConcerns.map((concern, index) => (
                <div
                  key={index}
                  className="bg-red-50 border border-red-200 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-gray-900">
                      {concern.concern}
                    </h4>
                    {getUrgencyBadge(concern.urgency)}
                  </div>
                  <p className="text-sm text-gray-600">{concern.action}</p>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Investigations */}
      {analysis.investigations &&
        Array.isArray(analysis.investigations) &&
        analysis.investigations.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Search className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Recommended Investigations
                </h3>
                <p className="text-sm text-gray-600">
                  Tests and imaging to confirm diagnoses
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {analysis.investigations.map((investigation, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-gray-900">
                      {investigation.test}
                    </h4>
                    {getUrgencyBadge(investigation.priority)}
                  </div>
                  <p className="text-sm text-gray-600">
                    {investigation.indication}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Specialist Referrals */}
      {analysis.specialistReferrals &&
        Array.isArray(analysis.specialistReferrals) &&
        analysis.specialistReferrals.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <UserCheck className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Specialist Referrals
                </h3>
                <p className="text-sm text-gray-600">
                  Recommended subspecialty consultations
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {analysis.specialistReferrals.map((referral, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-gray-900">
                      {referral.specialty}
                    </h4>
                    {getUrgencyBadge(referral.urgency)}
                  </div>
                  <p className="text-sm text-gray-600">{referral.indication}</p>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Monitoring Plan */}
      {analysis.monitoring && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Calendar className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Monitoring Plan
              </h3>
              <p className="text-sm text-gray-600">
                Follow-up schedule and parameters to track
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">
                Follow-up Interval
              </h4>
              <p className="text-sm text-gray-600">
                {analysis.monitoring.followUpInterval}
              </p>
            </div>
            {analysis.monitoring.parameters &&
              analysis.monitoring.parameters.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">
                    Parameters to Monitor
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {analysis.monitoring.parameters.map((param, index) => (
                      <span
                        key={index}
                        className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                      >
                        {param}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            {analysis.monitoring.redFlags &&
              analysis.monitoring.redFlags.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">
                    Red Flags to Watch
                  </h4>
                  <div className="space-y-1">
                    {analysis.monitoring.redFlags.map((flag, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                        <span className="text-sm text-gray-700">{flag}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
        </div>
      )}

      {/* Preventive Measures */}
      {analysis.preventiveMeasures &&
        Array.isArray(analysis.preventiveMeasures) &&
        analysis.preventiveMeasures.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-teal-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Preventive Measures
                </h3>
                <p className="text-sm text-gray-600">
                  Age and gender-appropriate preventive care
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {analysis.preventiveMeasures.map((measure, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-gray-900">
                      {measure.measure}
                    </h4>
                    <span className="bg-teal-100 text-teal-800 px-2.5 py-0.5 rounded-full text-xs font-medium">
                      {measure.timeline}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{measure.indication}</p>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Interactive Q&A Section */}
      {analysis.followUpQuestions && analysis.followUpQuestions.length > 0 && (
        <InteractiveQA
          questions={analysis.followUpQuestions}
          onSubmitAnswers={onSubmitAnswers}
          patientName={patientName}
        />
      )}

      {/* Refined Analysis Section */}
      {refinedAnalysis && (
        <RefinedAnalysis
          refinedData={refinedAnalysis}
          questions={analysis.followUpQuestions || []}
          answers={qaAnswers || {}}
        />
      )}
    </div>
  );
};

export default AIAnalysisRenderer;
